import FrequencyTrendLimitLogFactory from "../factories/FrequencyTrendLimitLogFactory";
import Controller from "@/controllers/index";
import DateTimeHandlerService from "@/services/common/date-time/DateTimeHandlerService";

/**
 * Busca a lista do log de modificações nos limites e notificações das tendências de frequência.
 * @param startDateTime  Data de inicio da pesquisa
 * @param endDateTime  Data do fim da pesquisa
 * @returns {Array} Lista de log dos limites e notificações das tendencias de frequência.
 */
const GetFrequencyTrendLimitNotificationLogs = async function(startDateTime = "", endDateTime = "")
{
	const resp = await Controller.FrequencyTrendLimitNotificationLogs.Get(startDateTime, endDateTime);

	if (resp.status === 204) return [];

	const logs = resp.data.map((log, index) => { return FrequencyTrendLimitLogFactory.LogView(log, index); });
	return logs;
};

/**
 * Ordena um array de objetos com base em vários campos.
 *
 * @param items - O array de objetos a ser ordenado.
 * @param fields - Os campos pelos quais a ordenação será feita.
 * Cada elemento é o nome de um campo nos objetos e sua ordem define a prioridade da ordenação.
 * @param isDesc - Um array de booleanos correspondente a cada campo.
 * Se for "true" a ordem  é decrescente e "false" para ordem crescente.
 * @returns {Array} O array de objetos ordenado.
 *
 * @example
 * const items = [
 * { nome: "Bob", dateTime: "2024-01-01" },
 * { nome: "Alice", dateTime: "2023-12-31" },
 * { nome: "Alice", dateTime: "2023-11-31" }
 * ];
 * const sortedItems = TableSort(items, ["nome", "dateTime"], [false, true]);
 * output:
 * { nome: "Alice", dateTime: "2023-12-31" },
 * { nome: "Alice", dateTime: "2023-11-30" };
 * { nome: "Bob", dateTime: "2024-01-01" },
 */
const TableSort = function(items = [ {} ], fields = [ "" ], isDesc = [ false ])
{
	if (!fields) return items;

	items.sort((a, b) =>
	{
		let result = 0;

		for (let i = 0; i < fields.length; i++)
		{
			const field = fields[i];
			const sortParms = [ a[field], b[field], isDesc[i] ];

			if (field === "dateTime")
			{
				result = SortTableByDate(...sortParms);
			}
			else if (field === "hasEmailToShow")
			{
				result = SortTableByBoolean(...sortParms);
			}
			else if (typeof a[field] !== "undefined")
			{
				result = SortTableByStr(...sortParms);
			}
			if (result !== 0) return result;
		}
		return 0;
	});
	return items;
};

/**
 * Compara duas strings de data e retorna um valor numérico indicando sua ordem relativa.
 *
 * @param dateA - A primeira data a ser comparada.
 * @param dateB - A segunda data a ser comparada.
 * @param isDesc - Se deve ordenar em ordem decrescente. O padrão é `false` (crescente).
 * @returns {number} Um valor negativo se `dateA` for anterior a `dateB`,
 * um valor positivo se `dateA` for posterior a `dateB`, ou `0` se forem iguais.
 */
const SortTableByDate = function(dateA = "", dateB = "", isDesc = false)
{
	const format = DateTimeHandlerService.GetDateTimeFormat();
	const firstDate = DateTimeHandlerService.GetMomentObject(dateA, format);
	const secondDate = DateTimeHandlerService.GetMomentObject(dateB, format);
	if (isDesc)
		return firstDate - secondDate;

	return secondDate - firstDate;
};

/**
 * Compara duas strings e retorna um valor numérico indicando sua ordem relativa.
 *
 * @param strA - A primeira string a ser comparada.
 * @param strB - A segunda string a ser comparada.
 * @param isDesc - Se deve ordenar em ordem decrescente. `true` para decrescente, `false` para crescente.
 * @returns {number} Um valor negativo se `strA` for menor que `strB`,
 * um valor positivo se `strA` for maior que `strB`, ou `0` se forem iguais.
 */
const SortTableByStr = function(strA = "", strB = "", isDesc = false)
{
	if (isDesc)
		return strA.toLowerCase().localeCompare(strB.toLowerCase(), undefined, { ignorePunctuation: true });

	return strB.toLowerCase().localeCompare(strB.toLowerCase(), undefined, { ignorePunctuation: true });
};

/**
 * Compara dois valores booleanos e retorna um valor numérico indicando sua ordem relativa.
 *
 * @param boolA - O primeiro valor booleano a ser comparado.
 * @param boolB - O segundo valor booleano a ser comparado.
 * @param isDesc - Se deve ordenar em ordem decrescente. `true` para decrescente, `false` para crescente.
 * @returns {number} Um valor negativo se `boolA` for menor que `boolB`,
 * um valor positivo se `boolA` for maior que `boolB`, ou `0` se forem iguais.
 */
const SortTableByBoolean = function(boolA = false, boolB = false, isDesc = false)
{
	if (isDesc)
		return boolA - boolB;
	return boolB - boolA;
};

export default { GetFrequencyTrendLimitNotificationLogs, TableSort };